import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, FormControlLabel, Checkbox, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, IconButton, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const INSURER_ID = 'pacific_cross_blue_royale';
const BRAND_COLOR = '#1565c0';
const PREMIUM_ROW_COLOR = '#ffcc80';
const SELECTED_PREMIUM_ROW_COLOR = '#ffb74d';

// Each plan is one whole plan option - dental/vision are optional add-ons only some
// plans support, matching the rate provider's per-plan config (Dental Rates/Vision Rates).
// The rate provider prices all three plans in one call, so switching Plan Type doesn't
// need a re-fetch - only the field read off the already-fetched response changes.
const PLANS = [
    { planId: 'pacific_cross_blue_royale_plan_a', tierId: 'plan_a', label: 'Plan A', rateField: 'plan_a_with_travel_plus', dentalField: 'dental_plan_a', hasVision: false },
    { planId: 'pacific_cross_blue_royale_plan_b', tierId: 'plan_b', label: 'Plan B', rateField: 'plan_b_with_travel_plus', dentalField: 'dental_plan_b', hasVision: true },
    { planId: 'pacific_cross_blue_royale_plan_c', tierId: 'plan_c', label: 'Plan C', rateField: 'plan_c_with_travel_plus', dentalField: null, hasVision: false },
];

const RATE_FIELDS = [
    'plan_a_with_travel_plus', 'plan_b_with_travel_plus', 'plan_c_with_travel_plus',
    'dental_plan_a', 'dental_plan_b', 'vision_plan_b', 'personal_accident_rate',
];

const CLIENT_TYPE_ITEMS = ['Individual', 'Group'];

function formatRate(rate) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return '$' + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isRateAvailable(rate) {
    return typeof rate === 'number' && !isNaN(rate);
}

// Same as PacificCrossBlueRoyale_Rates' getRenewalPercent
function getRenewalPercent(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

// Status strings (eg. "Not available" for a client outside the 16-60 Personal Accident
// age range, or "Unavailable" for an unsupported payment frequency) get muted styling so
// they read as a status rather than a dollar amount.
function RateValue({ rate }) {
    const text = formatRate(rate);
    if (isRateAvailable(rate)) return text;
    return <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>{text}</span>;
}

export default function PacificCrossBlueRoyale_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const config = appConfig?.content?.specifics?.[INSURER_ID] || {};
    const deductibleRates = config['Deductibles'] || {};
    const paymentFrequencyCharges = config['Payment Frequency Surcharge'] || {};
    const personalAccidentOptions = config['Personal Accident Options'] || {};

    // Deductible options come from the config's keys, so a rate sheet update shows up
    // here without a code change.
    const deductibleItems = ['Nil', ...Object.keys(deductibleRates)];
    const personalAccidentCoverageItems = ['No', ...Object.keys(personalAccidentOptions)];

    // The rate provider only quotes Annually - every other frequency is derived on the
    // frontend from the surcharge the rate sheet's config carries.
    const otherPaymentFrequencies = Object.keys(paymentFrequencyCharges).filter(f => f !== 'Annually');
    const paymentFrequencyItems = ['Annually', ...(otherPaymentFrequencies.length > 0 ? otherPaymentFrequencies : ['Semi Annually'])];

    const [selectedPlan, setSelectedPlan] = useState('pacific_cross_blue_royale_plan_a');
    const [clientType, setClientType] = useState(CLIENT_TYPE_ITEMS[0]);
    const [treatmentAreaLimitation, setTreatmentAreaLimitation] = useState(false);
    const [dentalBenefit, setDentalBenefit] = useState(false);
    const [visionBenefit, setVisionBenefit] = useState(false);
    const [personalAccidentCoverage, setPersonalAccidentCoverage] = useState('No');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: deductible string }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedPlanObj = PLANS.find(p => p.planId === selectedPlan) || PLANS[0];
    const isGroupClientType = clientType === 'Group';
    const isDentalActive = dentalBenefit && !!selectedPlanObj.dentalField;
    const isVisionActive = visionBenefit && selectedPlanObj.hasVision && isGroupClientType;

    function getDefaultQuoteDeductibles() {
        const defaults = {};
        (quotes || []).forEach(q => { defaults[q.name] = 'Nil'; });
        return defaults;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            setSelectedPlan(editingPlan.plan_id || 'pacific_cross_blue_royale_plan_a');
            setClientType(editingPlan.payloads?.client_type || CLIENT_TYPE_ITEMS[0]);
            setTreatmentAreaLimitation(!!editingPlan.payloads?.treatment_area_limitation);
            setDentalBenefit(!!editingPlan.payloads?.dental_benefit);
            setVisionBenefit(!!editingPlan.payloads?.vision_benefit);
            setPersonalAccidentCoverage(editingPlan.payloads?.personal_accident_coverage || 'No');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setDiscount(String(editingPlan.payloads?.discount ?? '0'));
            setLoadingPercent(String(editingPlan.payloads?.loading ?? '0'));

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || 'Nil';
            });
            setQuoteDeductibles(deductibles);
            setDeductibleErrors({});

            // Reconstruct ratesData from stored per-field premiums (saved before any discount)
            // - Plan Type is locked while editing, so only the saved plan's fields are needed.
            const plan = PLANS.find(p => p.planId === editingPlan.plan_id) || PLANS[0];
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    const raw = {};
                    raw[plan.rateField] = premium?.tier_premium ?? null;
                    if (plan.dentalField) raw[plan.dentalField] = premium?.dental_premium ?? null;
                    if (plan.hasVision) raw['vision_plan_b'] = premium?.vision_premium ?? null;
                    raw['personal_accident_rate'] = premium?.personal_accident_premium ?? null;
                    return raw;
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
        } else if (open && !editingPlan) {
            setSelectedPlan('pacific_cross_blue_royale_plan_a');
            setClientType(CLIENT_TYPE_ITEMS[0]);
            setTreatmentAreaLimitation(false);
            setDentalBenefit(false);
            setVisionBenefit(false);
            setPersonalAccidentCoverage('No');
            setPaymentFrequency('Annually');
            setDiscount('0');
            setLoadingPercent('0');
            setQuoteDeductibles(getDefaultQuoteDeductibles());
            setDeductibleErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Plan C has no Dental add-on and only Plan B offers Vision - drop a benefit that no
    // longer applies to the newly selected plan. The rate provider already priced every
    // plan in one call, so switching Plan Type doesn't clear the fetched rates.
    function handlePlanChange(planId) {
        const nextPlan = PLANS.find(p => p.planId === planId) || PLANS[0];
        setSelectedPlan(nextPlan.planId);
        if (!nextPlan.dentalField) setDentalBenefit(false);
        if (!nextPlan.hasVision) setVisionBenefit(false);
    }

    // Vision only applies to the Group client type - it's a display gate, not something
    // sent to the rate provider, so switching it doesn't need a re-fetch either.
    function handleClientTypeChange(value) {
        setClientType(value);
        if (value !== 'Group') setVisionBenefit(false);
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select a deductible for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { [INSURER_ID]: quoteDeductibles[quote.name] || 'Nil' },
                            insurers: [INSURER_ID],
                            specifics: {
                                [INSURER_ID]: {
                                    treatment_area_limitation: treatmentAreaLimitation ? 'Yes' : 'No',
                                    personal_accident_coverage: personalAccidentCoverage,
                                },
                            },
                            specific_area_of_coverage: { [INSURER_ID]: 'Worldwide' },
                            rates_version_per_insurer: { [INSURER_ID]: selectedRatesVersion?.[INSURER_ID] || '' },
                            specific_currency: { [INSURER_ID]: 'USD' },
                            specific_payment_frequency: { [INSURER_ID]: 'Annually' },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.[INSURER_ID] }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(RATE_FIELDS.map(f => [f, r[f]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Pacific Cross Blue Royale rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    function getRawRate(clientIndex, field, quoteUid) {
        const raw = ratesData?.[quoteUid]?.rawRates?.[clientIndex];
        if (!raw || !field) return null;
        const val = raw[field];
        return val === undefined ? null : val;
    }

    // The deductible and Treatment Area Limitation discounts are already applied
    // server-side to this field.
    function getPlanRate(clientIndex, quoteUid) {
        return getRawRate(clientIndex, selectedPlanObj.rateField, quoteUid);
    }

    // Renewal Discount/Loading only scales the plan rate - the optional benefits are flat
    // add-ons. Same as PacificCrossBlueRoyale_Rates' getPremiumAfterDiscount.
    function getPlanRateAfterDiscount(clientIndex, quoteUid) {
        const rate = getPlanRate(clientIndex, quoteUid);
        if (!isRateAvailable(rate)) return rate;
        return rate * getRenewalPercent(discount, loadingPercent);
    }

    function getOptionalRate(clientIndex, benefit, quoteUid) {
        if (benefit === 'dental') {
            if (!isDentalActive) return 0;
            return getRawRate(clientIndex, selectedPlanObj.dentalField, quoteUid);
        }
        if (benefit === 'vision') {
            if (!isVisionActive) return 0;
            return getRawRate(clientIndex, 'vision_plan_b', quoteUid);
        }
        if (benefit === 'personal_accident') {
            if (personalAccidentCoverage === 'No') return 0;
            return getRawRate(clientIndex, 'personal_accident_rate', quoteUid);
        }
        return 0;
    }

    function getOptionalPremium(clientIndex, quoteUid) {
        let total = 0;
        ['dental', 'vision', 'personal_accident'].forEach(benefit => {
            const rate = getOptionalRate(clientIndex, benefit, quoteUid);
            if (isRateAvailable(rate)) total += rate;
        });
        return total;
    }

    function getFinalPremium(clientIndex, quoteUid) {
        const planRate = getPlanRateAfterDiscount(clientIndex, quoteUid);
        if (!isRateAvailable(planRate)) return planRate;
        return planRate + getOptionalPremium(clientIndex, quoteUid);
    }

    function getSubtotal(getRateForClient) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRateForClient(i);
            if (isRateAvailable(rate)) { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Annually is the only frequency the rate provider quotes - the rest are derived here
    // from the surcharge the rate sheet's config carries.
    function getPremiumPerPaymentFrequency(frequency, quoteUid, clientIndex = null) {
        const annual = clientIndex === null
            ? getSubtotal((idx) => getFinalPremium(idx, quoteUid))
            : getFinalPremium(clientIndex, quoteUid);
        if (!isRateAvailable(annual)) return annual;

        const surcharge = paymentFrequencyCharges[frequency] || 0;
        if (frequency === 'Semi Annually') {
            return (annual + (annual * surcharge)) / 2;
        }
        const installments = { 'Annually': 1, 'Quarterly': 4, 'Monthly': 12 }[frequency] || 1;
        return (annual + (annual * surcharge)) / installments;
    }

    function getPaymentFrequencyLabel(frequency) {
        if (frequency === 'Annually') return 'Annual Premium';
        const percent = ((paymentFrequencyCharges[frequency] || 0) * 100).toFixed(0);
        if (frequency === 'Semi Annually') return `Semi Annual Premium (${percent}%)`;
        return `${frequency} Premium (+${percent}%)`;
    }

    function buildPlanLabel() {
        const extras = [];
        if (isDentalActive) extras.push('Dental');
        if (isVisionActive) extras.push('Vision');
        return extras.length > 0 ? `${selectedPlanObj.label} + ${extras.join(' + ')}` : selectedPlanObj.label;
    }

    const optionalRows = [
        { key: 'dental', label: 'Dental', visible: isDentalActive },
        { key: 'vision', label: 'Vision', visible: isVisionActive },
        { key: 'personal_accident', label: 'Personal Accident', visible: personalAccidentCoverage !== 'No' },
    ].filter(row => row.visible);

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const effectivePlanId = editingPlan ? editingPlan.plan_id : selectedPlanObj.planId;
        // plan_id identifies the tier and is shared by every instance of it; uid identifies this
        // specific plan instance so multiple plans of the same tier can coexist instead of
        // overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();

        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getPremiumPerPaymentFrequency(paymentFrequency, quote.uid, clientIndex);

                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: effectivePlanId,
                    planUid,
                    premium: {
                        insurer: INSURER_ID,
                        plan: selectedPlanObj.tierId,
                        plan_label: planLabel,
                        included_benefits: '',
                        deductible: quoteDeductibles[quote.name] || 'Nil',
                        area_of_coverage: 'Worldwide',
                        client_type: clientType,
                        treatment_area_limitation: treatmentAreaLimitation,
                        dental_benefit: dentalBenefit,
                        vision_benefit: visionBenefit,
                        personal_accident_coverage: personalAccidentCoverage,
                        currency: 'USD',
                        payment_frequency: paymentFrequency,
                        discount,
                        loading: loadingPercent,
                        premium_rate: rate ?? null,
                        // Component premiums are the rate sheet values before any discount
                        tier_premium: getPlanRate(clientIndex, quote.uid),
                        dental_premium: selectedPlanObj.dentalField ? getRawRate(clientIndex, selectedPlanObj.dentalField, quote.uid) : null,
                        vision_premium: selectedPlanObj.hasVision ? getRawRate(clientIndex, 'vision_plan_b', quote.uid) : null,
                        personal_accident_premium: getRawRate(clientIndex, 'personal_accident_rate', quote.uid),
                    },
                });
            });
        });

        const plan = {
            plan_id: effectivePlanId,
            uid: planUid,
            plan: selectedPlanObj.tierId,
            insurer: INSURER_ID,
            label: `Pacific Cross Blue Royale ${planLabel}`,
            payloads: {
                client_type: clientType,
                treatment_area_limitation: treatmentAreaLimitation,
                dental_benefit: dentalBenefit,
                vision_benefit: visionBenefit,
                personal_accident_coverage: personalAccidentCoverage,
                payment_frequency: paymentFrequency,
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || 'Nil'])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const renewalPercent = getRenewalPercent(discount, loadingPercent);
    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };
    const checkboxLabelSx = { mb: 0, display: 'block', whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: BRAND_COLOR,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    height: 60,
                }}>Pacific Cross Blue Royale</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', background: '#f8f9fa', px: 4, overflow: 'hidden' }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/pacific-cross-logo.avif`}
                            alt="Pacific Cross"
                            style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none', transform: 'scale(1.5)' }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type / Client Type — same as PacificCrossBlueRoyale_Rates */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                    <Box>
                        <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={selectedPlan}
                                disabled={loading || !!editingPlan}
                                // Every plan's rate comes back in one fetch, so switching just shows a different row
                                onChange={(e) => handlePlanChange(e.target.value)}
                                sx={selectSx}
                            >
                                {PLANS.map(plan => (
                                    <MenuItem key={plan.planId} value={plan.planId} sx={menuItemSx}>{plan.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box>
                        <Typography fontWeight={600} sx={{ fontSize: 15 }}>Client Type</Typography>
                        {CLIENT_TYPE_ITEMS.map(item => (
                            <FormControlLabel
                                key={item}
                                sx={checkboxLabelSx}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={clientType === item}
                                        onChange={() => handleClientTypeChange(item)}
                                        disabled={loading}
                                    />
                                }
                                label={item}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Optional Benefits */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                    <Box>
                        <FormControlLabel
                            sx={checkboxLabelSx}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={treatmentAreaLimitation}
                                    // Part of the rates request, so changing it invalidates the shown rates
                                    onChange={(e) => { setTreatmentAreaLimitation(e.target.checked); invalidateRates(); }}
                                    disabled={loading}
                                />
                            }
                            label="Treatment Area Limitation"
                        />

                        {!!selectedPlanObj.dentalField && (
                            <FormControlLabel
                                sx={checkboxLabelSx}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={dentalBenefit}
                                        onChange={(e) => setDentalBenefit(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Dental Benefits"
                            />
                        )}

                        {selectedPlanObj.hasVision && isGroupClientType && (
                            <FormControlLabel
                                sx={checkboxLabelSx}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={visionBenefit}
                                        onChange={(e) => setVisionBenefit(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Vision Benefits"
                            />
                        )}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center' }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Personal Accident Coverage</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={personalAccidentCoverage}
                            // Part of the rates request, so changing it invalidates the shown rates
                            onChange={(e) => { setPersonalAccidentCoverage(e.target.value); invalidateRates(); }}
                            items={personalAccidentCoverageItems}
                            disabled={loading}
                            isZeroDeductibleUniform={false}
                        />
                        <Box />
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: -1 }}>
                            Available for clients aged 16–60
                        </Typography>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={paymentFrequency}
                                disabled={loading}
                                // Derived from the annual premium on the frontend — no refetch
                                onChange={(e) => setPaymentFrequency(e.target.value)}
                                sx={selectSx}
                            >
                                {paymentFrequencyItems.map(frequency => (
                                    <MenuItem key={frequency} value={frequency} sx={menuItemSx}>{frequency}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-Quote Deductible */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductible per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 200 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...deductibleItems]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {quoteDeductibles[quote.name] || 'Nil'}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 260 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Plan premium - the deductible and Treatment Area Limitation discounts
                                                are already applied server-side */}
                                            <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                <TableCell sx={{ color: 'text.secondary' }}>{selectedPlanObj.label}</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx}>
                                                        <RateValue rate={getPlanRate(idx, quote.uid)} />
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    <RateValue rate={getSubtotal((idx) => getPlanRate(idx, quote.uid))} />
                                                </TableCell>
                                            </TableRow>

                                            {/* Plan premium after Renewal Discount & Loading */}
                                            {renewalPercent !== 1 && (
                                                <TableRow sx={{ backgroundColor: PREMIUM_ROW_COLOR }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>Premium after discount</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            <RateValue rate={getPlanRateAfterDiscount(idx, quote.uid)} />
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        <RateValue rate={getSubtotal((idx) => getPlanRateAfterDiscount(idx, quote.uid))} />
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {/* Optional benefits - flat add-ons */}
                                            {optionalRows.length > 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={clients.length + 2} sx={{ fontWeight: 600, background: BRAND_COLOR, color: '#fff' }}>Optional Premium</TableCell>
                                                </TableRow>
                                            )}
                                            {optionalRows.map((row, rowIdx) => (
                                                <TableRow key={row.key} sx={{ backgroundColor: rowIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{row.label}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            <RateValue rate={getOptionalRate(idx, row.key, quote.uid)} />
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        <RateValue rate={getSubtotal((idx) => getOptionalRate(idx, row.key, quote.uid))} />
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            {/* Premium per payment frequency — same rows as PacificCrossBlueRoyale_Rates;
                                                the selected Payment Frequency is the one saved */}
                                            {paymentFrequencyItems.map(frequency => {
                                                const isSelected = frequency === paymentFrequency;
                                                return (
                                                    <TableRow key={frequency} sx={{ backgroundColor: isSelected ? SELECTED_PREMIUM_ROW_COLOR : PREMIUM_ROW_COLOR }}>
                                                        <TableCell sx={{ fontWeight: isSelected ? 700 : 600 }}>{getPaymentFrequencyLabel(frequency)}</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx} sx={{ fontWeight: isSelected ? 700 : 400 }}>
                                                                <RateValue rate={getPremiumPerPaymentFrequency(frequency, quote.uid, idx)} />
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: isSelected ? 700 : 500 }}>
                                                            <RateValue rate={getPremiumPerPaymentFrequency(frequency, quote.uid)} />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId={INSURER_ID} />

                <RatesVersionSelect
                    insurer={INSURER_ID}
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
